import React, { useRef, useState, useEffect } from 'react';
import { Pencil, Eraser, Palette, Undo, Minus, ArrowRight, Type } from 'lucide-react';
import { toast } from 'sonner';

const DrawingCanvas = ({ backgroundImage, onSave, initialDrawing }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#000000');
  const [lineWidth, setLineWidth] = useState(2);
  const [ctx, setCtx] = useState(null);
  const [history, setHistory] = useState([]);
  const [startPos, setStartPos] = useState(null);
  const [tempCanvas, setTempCanvas] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    setCtx(context);

    // Create temporary canvas for preview
    const temp = document.createElement('canvas');
    setTempCanvas(temp);

    // Load background image
    if (backgroundImage) {
      const img = new Image();
      img.onload = () => {
        canvas.width = img.width || 800;
        canvas.height = img.height || 600;
        temp.width = canvas.width;
        temp.height = canvas.height;
        context.drawImage(img, 0, 0);
        
        // Load initial drawing if provided
        if (initialDrawing) {
          const savedImg = new Image();
          savedImg.onload = () => {
            context.drawImage(savedImg, 0, 0);
          };
          savedImg.src = initialDrawing;
        }
      };
      img.src = backgroundImage;
    } else {
      canvas.width = 800;
      canvas.height = 600;
      temp.width = 800;
      temp.height = 600;
      context.fillStyle = '#f8fafc';
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, [backgroundImage, initialDrawing]);

  const startDrawing = (e) => {
    if (!ctx) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Calculate proper coordinates accounting for canvas scaling
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    setIsDrawing(true);
    setStartPos({ x, y });

    if (tool === 'pen') {
      ctx.beginPath();
      ctx.moveTo(x, y);
    } else if (tool === 'text') {
      // Handle text tool
      const text = prompt('Enter label text:');
      if (text && text.trim()) {
        ctx.font = `${Math.max(16, lineWidth * 6)}px Inter, sans-serif`;
        ctx.fillStyle = color;
        ctx.textBaseline = 'top';
        ctx.fillText(text, x, y);
        
        // Don't auto-save, let user click Save Drawing button
        // Just add to history for undo functionality
        setHistory([...history, canvas.toDataURL()]);
        
        toast.success('Text added! Click "Save Drawing" to save.', {
          duration: 2000,
        });
      }
      setIsDrawing(false);
    } else if (tool === 'line' || tool === 'arrow') {
      // Save current canvas state before drawing preview
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
      tempCtx.drawImage(canvas, 0, 0);
    }
  };

  const draw = (e) => {
    if (!isDrawing || !ctx) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Calculate proper coordinates accounting for canvas scaling
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    if (tool === 'pen') {
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.lineTo(x, y);
      ctx.stroke();
    } else if (tool === 'eraser') {
      ctx.clearRect(x - 10, y - 10, 20, 20);
    } else if (tool === 'line' || tool === 'arrow') {
      // Preview for line and arrow - redraw everything
      if (!tempCanvas || !startPos) return;
      
      // Clear and restore base image
      const img = new Image();
      img.src = tempCanvas.toDataURL();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        
        // Draw preview
        ctx.strokeStyle = color;
        ctx.lineWidth = lineWidth;
        ctx.lineCap = 'round';
        ctx.fillStyle = color;
        
        if (tool === 'line') {
          ctx.beginPath();
          ctx.moveTo(startPos.x, startPos.y);
          ctx.lineTo(x, y);
          ctx.stroke();
        } else if (tool === 'arrow') {
          drawArrow(ctx, startPos.x, startPos.y, x, y, color, lineWidth);
        }
      };
    }
  };

  const drawArrow = (context, fromX, fromY, toX, toY, strokeColor, strokeWidth) => {
    const headLength = 15 + strokeWidth * 2; // Make arrowhead proportional to line width
    const angle = Math.atan2(toY - fromY, toX - fromX);
    
    context.strokeStyle = strokeColor;
    context.fillStyle = strokeColor;
    context.lineWidth = strokeWidth;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    
    // Draw the main line
    context.beginPath();
    context.moveTo(fromX, fromY);
    context.lineTo(toX, toY);
    context.stroke();
    
    // Draw arrowhead
    context.beginPath();
    context.moveTo(toX, toY);
    context.lineTo(
      toX - headLength * Math.cos(angle - Math.PI / 7),
      toY - headLength * Math.sin(angle - Math.PI / 7)
    );
    context.lineTo(
      toX - headLength * Math.cos(angle + Math.PI / 7),
      toY - headLength * Math.sin(angle + Math.PI / 7)
    );
    context.closePath();
    context.fill();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    
    if (ctx && (tool === 'line' || tool === 'arrow')) {
      // Save the final canvas state to temp
      const canvas = canvasRef.current;
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
      tempCtx.drawImage(canvas, 0, 0);
    } else if (ctx) {
      ctx.closePath();
      const canvas = canvasRef.current;
      setHistory([...history, canvas.toDataURL()]);
    }
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (canvas && onSave) {
      onSave(canvas.toDataURL());
      // Only show toast if explicitly clicking save button
      if (tool !== 'text') {
        toast.success('Drawing saved successfully!', {
          duration: 2000,
        });
      }
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || !ctx) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Redraw background if exists
    if (backgroundImage) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
      };
      img.src = backgroundImage;
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-4 bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex-wrap">
        <button
          data-testid="drawing-tool-pen"
          onClick={() => setTool('pen')}
          className={`p-3 rounded-lg transition-all ${
            tool === 'pen'
              ? 'bg-indigo-700 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          title="Pen"
        >
          <Pencil className="w-5 h-5" />
        </button>
        
        <button
          data-testid="drawing-tool-line"
          onClick={() => setTool('line')}
          className={`p-3 rounded-lg transition-all ${
            tool === 'line'
              ? 'bg-indigo-700 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          title="Straight Line"
        >
          <Minus className="w-5 h-5" />
        </button>
        
        <button
          data-testid="drawing-tool-arrow"
          onClick={() => setTool('arrow')}
          className={`p-3 rounded-lg transition-all ${
            tool === 'arrow'
              ? 'bg-indigo-700 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          title="Arrow"
        >
          <ArrowRight className="w-5 h-5" />
        </button>
        
        <button
          data-testid="drawing-tool-text"
          onClick={() => setTool('text')}
          className={`p-3 rounded-lg transition-all ${
            tool === 'text'
              ? 'bg-indigo-700 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          title="Text Label"
        >
          <Type className="w-5 h-5" />
        </button>
        
        <button
          data-testid="drawing-tool-eraser"
          onClick={() => setTool('eraser')}
          className={`p-3 rounded-lg transition-all ${
            tool === 'eraser'
              ? 'bg-indigo-700 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          title="Eraser"
        >
          <Eraser className="w-5 h-5" />
        </button>
        
        <div className="flex items-center gap-2 border-l border-slate-200 pl-4">
          <Palette className="w-5 h-5 text-slate-500" />
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="w-12 h-10 rounded cursor-pointer"
            data-testid="drawing-color-picker"
            title="Color"
          />
        </div>
        
        <div className="flex items-center gap-2 border-l border-slate-200 pl-4">
          <span className="text-sm text-slate-600">Width:</span>
          <input
            type="range"
            min="1"
            max="10"
            value={lineWidth}
            onChange={(e) => setLineWidth(e.target.value)}
            className="w-24"
            title="Line Width"
          />
          <span className="text-sm text-slate-600">{lineWidth}px</span>
        </div>

        <button
          data-testid="drawing-clear-btn"
          onClick={clearCanvas}
          className="ml-auto px-4 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all"
        >
          Clear
        </button>
        
        {onSave && (
          <button
            data-testid="drawing-save-btn"
            onClick={handleSave}
            className="px-4 py-2 rounded-lg bg-indigo-700 text-white hover:bg-indigo-800 transition-all"
          >
            Save Drawing
          </button>
        )}
      </div>

      {/* Canvas */}
      <div className="bg-slate-50 rounded-xl border-2 border-dashed border-slate-300 p-4 overflow-auto">
        <canvas
          ref={canvasRef}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          className={`max-w-full bg-white ${tool === 'text' ? 'cursor-text' : 'canvas-cursor'}`}
          style={{ 
            border: '2px solid #cbd5e1'
          }}
          data-testid="drawing-canvas"
        />
      </div>
    </div>
  );
};

export default DrawingCanvas;